<template>
  <v-dialog
    :value="value"
    max-width="640"
    scrollable
    persistent
    content-class="dialog-border-radius apartment-analog-dialog"
    @input="onDialogInput"
  >
    <v-card>
      <v-card-title class="apartment-analog-dialog__title">
        <v-icon left color="primary">mdi-phone-settings</v-icon>
        Настройки квартиры {{ apartment }}
        <v-spacer />
        <v-btn icon :disabled="saving" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text v-if="formReady">
        <v-form ref="form" lazy-validation>
          <h6 class="apartment-analog-dialog__section mb-2">Звонок</h6>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-autocomplete
                v-model="form.ring_type"
                :items="ringTypes"
                item-text="text"
                item-value="value"
                label="Мелодия звонка"
                outlined
                dense
                hide-details
                :disabled="!canSave"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-autocomplete
                v-model="form.ring_volume"
                :items="ringVolumes"
                item-text="text"
                item-value="value"
                label="Громкость звонка"
                outlined
                dense
                hide-details
                :disabled="!canSave"
              />
            </v-col>
          </v-row>

          <h6 class="apartment-analog-dialog__section mb-2 mt-4">Аналоговая линия (В)</h6>
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model.number="form.analog_line_voltage_idle"
                label="Покой"
                type="number"
                step="0.1"
                outlined
                dense
                hide-details
                :disabled="!canSave"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model.number="form.analog_line_voltage_lifted"
                label="Трубка поднята"
                type="number"
                step="0.1"
                outlined
                dense
                hide-details
                :disabled="!canSave"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model.number="form.analog_line_voltage_button_pressed"
                label="Кнопка"
                type="number"
                step="0.1"
                outlined
                dense
                hide-details
                :disabled="!canSave"
              />
            </v-col>
          </v-row>

          <h6 class="apartment-analog-dialog__section mb-2 mt-4">Цифровая линия (В)</h6>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="form.digital_line_voltage_lifted"
                label="Трубка поднята"
                type="number"
                step="0.1"
                outlined
                dense
                hide-details
                :disabled="!canSave"
              />
            </v-col>
          </v-row>

          <h6 class="apartment-analog-dialog__section mb-2 mt-4">Аудио</h6>
          <v-row dense>
            <v-col cols="12">
              <div class="mb-1 text-caption">
                Громкость динамика (0–15): {{ form.audio_volume }}
              </div>
              <v-slider
                v-model="form.audio_volume"
                min="0"
                max="15"
                thumb-label
                :disabled="!canSave"
              />
            </v-col>
            <v-col cols="12">
              <div class="mb-1 text-caption">
                Чувствительность микрофона (0–15): {{ form.audio_mic }}
              </div>
              <v-slider
                v-model="form.audio_mic"
                min="0"
                max="15"
                thumb-label
                :disabled="!canSave"
              />
            </v-col>
            <v-col cols="12">
              <div class="mb-1 text-caption">
                Проксирование громкости трубки (0–15): {{ form.handset_proxy_volume }}
              </div>
              <v-slider
                v-model="form.handset_proxy_volume"
                min="0"
                max="15"
                thumb-label
                :disabled="!canSave"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text class="text-none" :disabled="saving" @click="close">Отмена</v-btn>
        <v-btn
          v-if="canSave"
          color="primary"
          class="text-none"
          :loading="saving"
          :disabled="!formReady"
          @click="save"
        >
          <v-icon left small>mdi-content-save-outline</v-icon>
          Сохранить
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';
import {
  APARTMENT_RING_TYPES,
  APARTMENT_RING_VOLUMES,
  mapApartmentAnalogFormFromApi,
} from './apartmentAnalogLineConsts';

export default {
  name: 'AnalogLineApartmentSettingsModal',
  props: {
    value: { type: Boolean, default: false },
    doorphoneId: { type: [String, Number], required: true },
    apartment: { type: [String, Number], default: null },
    initialData: { type: Object, default: null },
    canSave: { type: Boolean, default: true },
  },
  data () {
    return {
      ringTypes: APARTMENT_RING_TYPES,
      ringVolumes: APARTMENT_RING_VOLUMES,
      saving: false,
      formReady: false,
      form: {},
    };
  },
  watch: {
    value (open) {
      if (open) {
        this.syncFormFromProps();
      } else {
        this.formReady = false;
        this.form = {};
      }
    },
    initialData () {
      if (this.value) {
        this.syncFormFromProps();
      }
    },
  },
  methods: {
    ...mapActions({
      updateAnalogLineApartmentSettings: 'Doorphone/updateAnalogLineApartmentSettings',
    }),
    syncFormFromProps () {
      if (!this.initialData) {
        return;
      }
      this.form = mapApartmentAnalogFormFromApi(this.initialData, this.apartment);
      this.formReady = true;
    },
    validateForm () {
      const idle = Number(this.form.analog_line_voltage_idle);
      const lifted = Number(this.form.analog_line_voltage_lifted);
      const pressed = Number(this.form.analog_line_voltage_button_pressed);

      if (lifted <= idle) {
        this.$flash_warning('«Трубка поднята» (аналог) должна быть больше «Покой»');
        return false;
      }
      if (pressed <= lifted) {
        this.$flash_warning('«Кнопка» (аналог) должна быть больше «Трубка поднята»');
        return false;
      }
      return true;
    },
    buildPayload () {
      return {
        apartment: Number(this.form.apartment),
        ring_type: Number(this.form.ring_type),
        ring_volume: Number(this.form.ring_volume),
        analog_line_voltage_idle: Number(this.form.analog_line_voltage_idle),
        analog_line_voltage_lifted: Number(this.form.analog_line_voltage_lifted),
        analog_line_voltage_button_pressed: Number(this.form.analog_line_voltage_button_pressed),
        digital_line_voltage_lifted: Number(this.form.digital_line_voltage_lifted),
        audio_volume: Number(this.form.audio_volume),
        audio_mic: Number(this.form.audio_mic),
        handset_proxy_volume: Number(this.form.handset_proxy_volume),
      };
    },
    async save () {
      if (!this.validateForm()) {
        return;
      }

      this.saving = true;
      try {
        const body = await this.updateAnalogLineApartmentSettings({
          id: this.doorphoneId,
          data: this.buildPayload(),
        });
        if (body?.status === false) {
          this.$flash_warning(body.error || 'Ошибка сохранения');
          return;
        }
        this.$flash_success('Настройки квартиры сохранены');
        this.$emit('saved');
        this.close();
      } catch (err) {
        console.error(err);
        this.$flash_warning(
          err?.response?.data?.error || err?.response?.data?.message || 'Ошибка сохранения настроек квартиры',
        );
      } finally {
        this.saving = false;
      }
    },
    onDialogInput (open) {
      if (!open) {
        this.close();
      }
    },
    close () {
      this.$emit('input', false);
    },
  },
};
</script>

<style lang="scss" scoped>
.apartment-analog-dialog__title {
  font-size: 1.05rem;
  font-weight: 500;
}

.apartment-analog-dialog__section {
  font-size: 0.85rem;
  font-weight: 500;
  color: #546e7a;
}
</style>
